import axios from "axios";

export interface CifraSong {
  id?: number;
  title: string;
  artist: string;
  artistSlug: string;
  songSlug: string;
  artworkURL?: string | null;
  albumName?: string;
  hasVideo?: boolean;
  cifraclubUrl: string;
  imprimirUrl: string;
  imprimirUrlSimplificada?: string;
}

export interface CifraImportPrefill {
  title?: string;
  artist?: string;
  album?: string;
  youtubeLink?: string;
  youtubeLinks?: string[];
  charts?: string;
  artworkURL?: string;
  cifraclubUrl?: string;
  artistSlug?: string;
  songSlug?: string;
}

export function getCifraApiBase() {
  return (
    process.env.REACT_APP_CIFRA_API_URL ||
    (process.env.NODE_ENV === "development" ? "http://localhost:3002" : "")
  );
}

function endpoint(path: string) {
  const base = getCifraApiBase();
  const clean = path.replace(/^\//, "");
  return base ? `${base}/${clean}` : `/.netlify/functions/${clean}`;
}

export async function searchCifraSongs(query: string, limit = 30) {
  const res = await axios.get(endpoint("/cifraSearch"), {
    params: { q: query, limit },
  });
  return (res.data.songs || []) as CifraSong[];
}

export async function browseCifraArtistSongs(artist: string) {
  const res = await axios.get(endpoint("/cifraArtistSongs"), {
    params: { artist, order: "alphabetical" },
  });
  return (res.data.songs || []) as CifraSong[];
}

export type CifraChordVersion = "principal" | "simplificada";

function chordsUrlForVersion(song: CifraSong, version: CifraChordVersion) {
  return version === "simplificada" && song.imprimirUrlSimplificada
    ? song.imprimirUrlSimplificada
    : song.imprimirUrl;
}

export async function fetchCifraChords(
  song: CifraSong,
  version: CifraChordVersion,
): Promise<{ charts: string; notice?: string }> {
  const chordsUrlToUse = chordsUrlForVersion(song, version);
  try {
    const chordsRes = await axios.get(endpoint("/fetchChords"), {
      params: { url: chordsUrlToUse },
      validateStatus: (s) => s < 600,
    });
    if (chordsRes.status >= 400) {
      return {
        charts: "",
        notice:
          (chordsRes.data as { error?: string })?.error ||
          `Chords fetch failed (${chordsRes.status})`,
      };
    }
    const charts = (chordsRes.data as { chords?: string })?.chords || "";
    if (!charts.trim()) {
      return {
        charts: "",
        notice:
          "No chord text returned. You can paste it manually in Add Song.",
      };
    }
    return { charts };
  } catch (err: unknown) {
    const ax = err as {
      response?: { data?: { error?: string } };
      message?: string;
    };
    const connRefused =
      typeof ax.message === "string" && ax.message.includes("ECONNREFUSED");
    return {
      charts: "",
      notice: connRefused
        ? "Song Importer API is not reachable. Run `npm run cifra:server` in a terminal or use `npm run dev` with Netlify dev."
        : ax.response?.data?.error || ax.message || "Could not load chords.",
    };
  }
}

export async function fetchCifraYoutubeUrl(song: CifraSong) {
  try {
    const ytRes = await axios.get(endpoint("/fetchYoutubeUrl"), {
      params: { url: song.cifraclubUrl },
      validateStatus: (s) => s < 600,
    });
    if (ytRes.status < 400) {
      return (
        (ytRes.data as { youtubeUrl?: string | null })?.youtubeUrl || undefined
      );
    }
  } catch {
    /* optional */
  }
  return undefined;
}

export async function fetchCifraImportData(
  song: CifraSong,
  version: CifraChordVersion,
): Promise<{ prefill: CifraImportPrefill; notice?: string }> {
  const [{ charts, notice }, youtubeLink] = await Promise.all([
    fetchCifraChords(song, version),
    fetchCifraYoutubeUrl(song),
  ]);

  return {
    notice,
    prefill: {
      title: song.title,
      artist: song.artist,
      album: song.albumName,
      cifraclubUrl: song.cifraclubUrl,
      artistSlug: song.artistSlug,
      songSlug: song.songSlug,
      ...(charts.trim() ? { charts } : {}),
      ...(youtubeLink ? { youtubeLink } : {}),
      artworkURL: song.artworkURL || undefined,
    },
  };
}
